import {WB_CubeSourceDataSourceRow, WB_CustomerHierarchyRow, WB_ModelRow,
    WB_PeriodHierarchyRow, WB_PlanTypeRow, WB_ProductHierarchyRow} from "../PA.Core/PAdbSchemaRows";
import {DimensionType, IHierarchy, IHierarchyNode} from "../PA.Core/PA.Core.IF.Hierarchy";
import {ICubeSource, IDimensionCompositeKeys, IDimensionDistinctKeys, IKeyOptions} from "./PA.HyperRectangle.IF";
import {IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {BehaviorSubject} from "rxjs";


export interface ICubeSourceWithDataSources
    extends
        ICubeSource
{
    // Parameters
    setParameter(pName: string, value: (number|string)[] | number | string): void
    getParameter(pName: string): (number|string)[]
    readonly OnParameterChanged: BehaviorSubject<ICubeSourceParameterChangeEvent>

    setEnabled(bEnabled:boolean): void
    readonly bEnabled: boolean

    // DataSource
    getDataSource(sAlias: string): ICubeSourceDataSource
    readonly DataSources: ICubeSourceDataSource[]

    // Invalidate Loaded Rows
    purgeScope(scope: IRowScope, sAlias?: string | string[]): void

}


export interface ICubeSourceDataSource {
    //-- PreLoad Options before Initialization
    setLoadOptions(options: ILoadOptions): void
    
    readonly bIsLoaded: boolean
    readonly OnLoadComplete: Promise<void>


    readonly DataSourceName: string
    readonly Alias: string

    readonly CubeSource: ICubeSourceWithDataSources
    readonly CubeSourceDataSourceRow: WB_CubeSourceDataSourceRow
    readonly LoadProgressProvider: IProgressProvider



    //-- Row Metadata Functions
    getRowDimensionKeyField(dimensionType: DimensionType): string
    getRowMetricFieldIds(): string[],
    getRowMetricFieldMetaData(aFieldId: string): IDataSourceFieldMetaData
    getDimensionMetaData(dimensionType: DimensionType): IDataSourceDimensionMetaData


    //-- Row Functions
    readonly LoadedRows: IDataRow[]
    setKnownKeyOptions(options: IKnownKeyOptions): void
    add(rows: IDataRow[] | IDataRow): void
    remove(rows: IDataRow[] | IDataRow | number[] | number): void
    getLoadStatus(dimensionTypes?: DimensionType[]): ILoadStatus

    //-- Loading/ Unloading functions
    checkLoadScopeSync(scope: IRowScope): boolean
    getRowScope(scope: IRowScope): Promise<IDataRow[]>
    getLocalRowScope(scope: IRowScope): IDataRow[]
    purgeScope(scope: IRowScope): void



    //--
    OnRowsChanged: BehaviorSubject<IDataSourceOnRowsChangedEvent>
    OnLoadProcessingComplete: BehaviorSubject<ICubeSourceDataSource>

    // Query Key Function
    getDistinctKeys(dimensionTypes: DimensionType[], options: IKeyOptions): Promise<IDimensionDistinctKeys>
    getCompositeKeys(dimensionTypes: DimensionType[], options: IKeyOptions): Promise<IDimensionCompositeKeys>

}



export enum DataSourceRowsChange {
    Loaded="Loaded",
    Added="Added",
    Removed="Removed"
}
export interface IDataSourceOnRowsChangedEvent {
    DataSource: ICubeSourceDataSource,
    Change: DataSourceRowsChange,
    Rows: IDataRow[] | number[]
}

export interface ICubeSourceParameterChangeEvent {
    CubeSource: ICubeSourceWithDataSources,
    Name: string,
    Value: (number|string)[]
}

//<editor-fold desc="CubeSourceWithDataSources Metadata Supporting Interfaces">

export interface IDataSourceFieldMetaData {
    FieldId: string,
    FieldName: string,
    FieldDescription: string,
    FieldType: string,
    FieldSort: number,
    MetricId: string
}

export interface IDataSourceDimensionMetaData {
    DimensionType: DimensionType
    LeafLevel: string
}

//</editor-fold>


//<editor-fold desc="CubeSourceWithDataSources Supporting Interfaces">

export interface ICubeSourceHierarchyPolicy {
    readonly PolicyId: string,
    getHierarchyProperties(dimType: DimensionType, options: IDimensionPolicyOptions): Promise<ICubeSourcePolicyHierarchyProperties>
}



export interface ICubeSourcePolicyHierarchyProperties {
    HierarchyId: number | string
    LeafLevel: string,
    EffectiveStartDt: Date,
    EffectiveEndDt: Date
}

export interface IDimensionPolicyOptions {
    CubeSource: ICubeSourceWithDataSources,
    WB_PLANM_ID?: number
    WB_PlanTypeRow?: WB_PlanTypeRow
    WB_MODEL_ID?: number
    WB_ModelRow?: WB_ModelRow
    WB_CUSTH_ID?: number
    WB_CustomerHierarchyRow?: WB_CustomerHierarchyRow
    Customer_Level?: string
    WB_PRDH_ID?: number
    WB_ProductHierarchyRow?: WB_ProductHierarchyRow
    Product_Level?: string
    WB_PERIODH_ID?: number,
    WB_PeriodHierarchyRow?: WB_PeriodHierarchyRow
    Period_Level?: string
    WB_PERIOD_ID?: number
    EffectiveStartDt?: Date
    EffectiveEndDt?: Date
    [id:string]: any
}


//</editor-fold>

//<editor-fold desc="CubeSource DataSource Supporting Interfaces">
export interface IDataRow {
    [columnId: string]: any
}

export interface IRowScope {
    [dimensionType: string]: {
        //-- bAll True or
        bAll?: boolean,
        //-- bAll False,
        Hierarchy?: IHierarchy,
        HierarchyNodes?: IHierarchyNode[]
    }
}
//</editor-fold>

//<editor-fold desc="CubeSource Loading">

export interface ILoadOptions {
    [dimensionType: string]: ILoadDimensionOptions
}

export interface ILoadDimensionOptions {
    bEnableLazyLoading?: boolean,
    InitialLeafKeys?: (number | string)[]
}

export interface IKnownKeyOptions {
    [dimensionType: string]: IKnownKeyDimensionOptions
}

export interface IKnownKeyDimensionOptions {
    bUseKnownKeys?: boolean,
    KnownLeafKeys?: (number | string)[]
}

export interface IDimensionLoadStatus {
    bLazyLoadingEnabled: boolean
    dctLeafIdLoadedById: { [id: number | string]: number | string }
    bLoadAllLoaded: boolean
    dctLeafIdQueuedById: { [id: number | string]: number | string }
    bLoadAllQueued: boolean
}
export interface ILoadStatus {
    [dimensionType: string]: IDimensionLoadStatus
}

//</editor-fold>
